import React, { useState } from 'react';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import { useModalManager } from 'common/Modal';
import AsyncAssignmentModal from 'staffer/AssignmentModal/AsyncAssignmentModal';
import { TCITooltip } from '@/components/ui/Tooltip';
import AssignmentMadeTooltipContent from '@/components/common/AssignmentMadeTooltipContent';
import Icon from '../../ui/Icon';
import cn from '../../../utils/cn';
import styles from './index.module.scss';

const comingSoonText = 'Assigning coming soon';

const ComingSoonButton = React.forwardRef((props, ref) => (
  <span ref={ref} {...props}>
    <button disabled className={styles.icon} type="button">
      <i aria-hidden="true" className={`fa fa-calendar ${styles.disabled}`} />
      <span className="sr-only">{comingSoonText}</span>
    </button>
  </span>
));
ComingSoonButton.displayName = 'ComingSoonButton';

const AssignmentTooltipContent = ({ disabled, isAssigned }) => {
  if (disabled) return I18n.t('no_access_to_lesson');
  if (!isAssigned) return 'Create Assignment';

  return <AssignmentMadeTooltipContent />;
};

AssignmentTooltipContent.propTypes = {
  disabled: PropTypes.bool.isRequired,
  isAssigned: PropTypes.bool.isRequired,
};

const AssignmentShortcut = ({
  disabled,
  ...props
}) => {
  const [isAssigned, setIsAssigned] = useState(props.isAssigned);
  const modalManager = useModalManager();

  if (props.comingSoon) {
    return (
      <TCITooltip side="bottom">
        <TCITooltip.Trigger asChild>
          <ComingSoonButton />
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          {comingSoonText}
        </TCITooltip.Content>
      </TCITooltip>
    );
  }

  return (
    <React.Fragment>
      <TCITooltip side="bottom">
        <TCITooltip.Trigger asChild>
          <button
            aria-haspopup="dialog"
            aria-label="Create Assignment"
            className={`${styles.icon} ${isAssigned ? styles.assigned : ''} ${disabled && styles.disabled} !tw-mr-0`}
            disabled={disabled}
            type="button"
            onClick={(e) => {
              if (!disabled) {
                e.currentTarget.blur();
                modalManager.open();
              }
            }}
          >
            <Icon
              className={cn({ 'tw-text-green': isAssigned },
                { 'tw-text-tci-purple': !isAssigned })}
              icon={isAssigned ? 'Assigned' : 'Assign'}
              size={30}
            />
            <span className="sr-only">Create Assignment</span>
          </button>
        </TCITooltip.Trigger>
        <TCITooltip.Content>
          <AssignmentTooltipContent disabled={disabled} isAssigned={isAssigned} />
        </TCITooltip.Content>
      </TCITooltip>

      <AsyncAssignmentModal
        closeModal={modalManager.close}
        handlePostSave={() => setIsAssigned(true)}
        initialLessonId={props.unitId || props.lessonId}
        initialValues={props.initialValues}
        modalIsOpen={modalManager.isOpen}
        program={props.program}
        stafferId={props.stafferId}
      />
    </React.Fragment>
  );
};

AssignmentShortcut.propTypes = {
  comingSoon: PropTypes.bool,
  disabled: PropTypes.bool,
  initialValues: PropTypes.shape({
    modelType: PropTypes.string.isRequired,
    modelsToAssign: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }))
    ]),
  }).isRequired,
  isAssigned: PropTypes.bool.isRequired,
  lessonId: PropTypes.number,
  program: PropTypes.shape({
    id: PropTypes.number.isRequired,
    science: PropTypes.bool.isRequired
  }).isRequired,
  stafferId: PropTypes.number.isRequired,
  unitId: PropTypes.number,
};

AssignmentShortcut.defaultProps = {
  comingSoon: false,
  disabled: false,
};

export default AssignmentShortcut;
